// ABOUTME: The fetch client for the talent's interview on pro-core: the redeem of an email link, and one InterviewApi
// ABOUTME: over either the guest session or the Auth0 token, with the error mapping the card and the guest page branch on.
import { ApiError, ResponseError } from 'utils/api';
import { env } from '../../constants';
import {
  GUEST_INTERVIEWS_API_URL,
  INTERVIEW_NOT_FOUND_ERROR_CODE,
  INTERVIEW_SESSION_EXPIRED_ERROR_CODE,
  INTERVIEW_UNKNOWN_ERROR_CODE,
  INTERVIEWS_UNAVAILABLE_ERROR_CODE,
  TALENT_INTERVIEWS_API_URL
} from './constants';
import {
  InterviewApi,
  InterviewCardData,
  InterviewGuestSession,
  InterviewJoinPass
} from './types';

const GUEST_URL = `${env.VITE_API_BASE_URL}/${GUEST_INTERVIEWS_API_URL}`;
const TALENT_URL = `${env.VITE_API_BASE_URL}/${TALENT_INTERVIEWS_API_URL}`;

/** The structured body, or an empty one: some refusals (a 401 from the security chain) carry none. */
const readError = async (response: Response): Promise<ResponseError> => {
  try {
    const json = (await response.json()) as Partial<ResponseError>;
    return { code: json.code ?? '', values: json.values ?? {} };
  } catch {
    return { code: '', values: {} };
  }
};

/**
 * Every failure as an ApiError with a code the screens branch on.
 *  - 401/403 → session-expired: the guest session lapsed or was replaced (re-redeem), or the token is not a talent's
 *  - 404     → pro-core's error.interview.not-found as it is; any other 404 is PRO_INTERVIEWS off (error.not-found)
 *  - else    → the { code, values } pro-core sent, or unknown when it sent none
 */
export const toInterviewError = async (
  response: Response
): Promise<ApiError> => {
  if (response.status === 401 || response.status === 403) {
    return new ApiError({
      code: INTERVIEW_SESSION_EXPIRED_ERROR_CODE,
      values: {}
    });
  }
  const error = await readError(response);
  if (
    response.status === 404 &&
    error.code !== INTERVIEW_NOT_FOUND_ERROR_CODE
  ) {
    return new ApiError({
      code: INTERVIEWS_UNAVAILABLE_ERROR_CODE,
      values: {}
    });
  }
  return new ApiError(
    error.code ? error : { code: INTERVIEW_UNKNOWN_ERROR_CODE, values: {} }
  );
};

const unwrap = async <T>(response: Response): Promise<T> => {
  const json = (await response.json()) as { data: T };
  return json.data;
};

/**
 * POST /guest/interviews/redeem: the link token from `?t=` for a 60-minute guest session. No Authorization header:
 * the link token is the credential on this one call. The body is `{ token }`, not the `{ t }` of the guest forms.
 */
export const redeemInterviewLink = async (
  linkToken: string
): Promise<InterviewGuestSession> => {
  const response = await fetch(`${GUEST_URL}/redeem`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token: linkToken })
  });
  if (!response.ok) {
    throw await toInterviewError(response);
  }
  return unwrap<InterviewGuestSession>(response);
};

interface ClientOptions {
  scope: InterviewApi['scope'];
  identity: InterviewApi['identity'];
  baseUrl: string;
  /** The bearer token read at call time, so a token a recovery just minted is the one sent. */
  getToken: () => string | null | undefined;
  /**
   * Called once on a 401/403 to mint a fresh token (the guest page re-redeems its link); the request is then sent
   * again with it. Null means the link itself is dead and the page has already switched to the screen saying so.
   */
  recoverToken?: () => Promise<string | null>;
}

const createInterviewApi = ({
  scope,
  identity,
  baseUrl,
  getToken,
  recoverToken
}: ClientOptions): InterviewApi => {
  const send = async <T>(
    method: 'GET' | 'POST',
    path: string,
    body?: unknown
  ): Promise<T> => {
    const request = (token: string | null | undefined) =>
      fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(body !== undefined ? { 'Content-Type': 'application/json' } : {})
        },
        body: body !== undefined ? JSON.stringify(body) : undefined
      });

    let response = await request(getToken());
    if ((response.status === 401 || response.status === 403) && recoverToken) {
      const fresh = await recoverToken();
      if (fresh) {
        response = await request(fresh);
      }
    }
    if (!response.ok) {
      throw await toInterviewError(response);
    }
    return unwrap<T>(response);
  };

  const card = (method: 'GET' | 'POST', path: string, body?: unknown) =>
    send<InterviewCardData>(method, path, body);

  return {
    scope,
    identity,
    isReady: !!getToken(),
    getCard: refId => card('GET', `/${refId}`),
    hold: (refId, body) => card('POST', `/${refId}/holds`, body),
    confirmHold: refId => card('POST', `/${refId}/holds/confirm`),
    propose: (refId, body) => card('POST', `/${refId}/proposals`, body),
    // Only `true` is accepted: "Ich kann nicht" is a move, not attending=false.
    confirmAttendance: refId =>
      card('POST', `/${refId}/attendance`, { attending: true }),
    move: (refId, body) => card('POST', `/${refId}/move`, body),
    cancel: (refId, body) => card('POST', `/${refId}/cancel`, body),
    rebook: refId => card('POST', `/${refId}/rebook`),
    join: refId => send<InterviewJoinPass>('GET', `/${refId}/join`)
  };
};

/** The email-link variant: Bearer is the guest session token, re-redeemed once when it has lapsed. */
export const createGuestInterviewApi = (
  getSessionToken: () => string | null,
  recoverSession: () => Promise<string | null>
): InterviewApi =>
  createInterviewApi({
    scope: 'guest',
    identity: null,
    baseUrl: GUEST_URL,
    getToken: getSessionToken,
    recoverToken: recoverSession
  });

/** The signed-in variant: Bearer is the Auth0 access token, which the Auth0 provider renews itself. */
export const createTalentInterviewApi = (
  token: string | undefined
): InterviewApi =>
  createInterviewApi({
    scope: 'talent',
    identity: token ?? null,
    baseUrl: TALENT_URL,
    getToken: () => token
  });
